import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import {
  ArrowLeft,
  Watch,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { wearableApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';

function GarminConnectForm({ onSuccess }: { onSuccess: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await wearableApi.connect({ username, password });

      if (result.needsMfa) {
        setNeedsMfa(true);
        setError(null);
      } else {
        setUsername('');
        setPassword('');
        onSuccess();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Yhdistäminen epäonnistui');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await wearableApi.verifyMfa(mfaCode);
      setUsername('');
      setPassword('');
      setMfaCode('');
      setNeedsMfa(false);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'MFA-vahvistus epäonnistui');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setNeedsMfa(false);
    setMfaCode('');
    setError(null);
  };

  // MFA verification form
  if (needsMfa) {
    return (
      <form onSubmit={handleMfaVerify} className="space-y-4">
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-blue-700 dark:text-blue-400 text-sm">
          MFA-koodi on lähetetty sähköpostiisi. Syötä koodi jatkaaksesi.
        </div>

        <div>
          <label htmlFor="mfaCode" className="label">
            MFA-koodi
          </label>
          <input
            type="text"
            id="mfaCode"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            className="input text-center text-2xl tracking-widest"
            placeholder="123456"
            maxLength={6}
            required
            disabled={isLoading}
            autoFocus
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="btn btn-secondary flex-1"
          >
            Peruuta
          </button>
          <button
            type="submit"
            disabled={isLoading || !mfaCode}
            className="btn btn-primary flex-1"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Vahvistetaan...
              </>
            ) : (
              'Vahvista'
            )}
          </button>
        </div>
      </form>
    );
  }

  // Normal login form
  return (
    <form onSubmit={handleConnect} className="space-y-4">
      <div>
        <label htmlFor="username" className="label">
          Garmin-käyttäjätunnus
        </label>
        <input
          type="text"
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          className="input"
          placeholder="Sähköposti tai käyttäjänimi"
          required
          disabled={isLoading}
        />
      </div>

      <div>
        <label htmlFor="password" className="label">
          Salasana
        </label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input"
          placeholder="Garmin-salasana"
          required
          disabled={isLoading}
        />
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading || !username || !password}
        className="btn btn-primary w-full"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Yhdistetään...
          </>
        ) : (
          'Yhdistä Garmin Connect'
        )}
      </button>

      <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
        Tunnuksesi salataan ja tallennetaan turvallisesti.
        Emme jaa tietojasi kolmansille osapuolille.
      </p>
    </form>
  );
}

function ConnectionStatus() {
  const queryClient = useQueryClient();

  const { data: connection, isLoading, refetch } = useQuery({
    queryKey: ['wearable-connection'],
    queryFn: wearableApi.getConnection,
  });

  const disconnectMutation = useMutation({
    mutationFn: wearableApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wearable-connection'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: wearableApi.sync,
    onSuccess: () => {
      refetch();
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (!connection) {
    return (
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full">
            <Watch className="h-6 w-6 text-gray-500" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">
              Garmin Connect
            </h3>
            <p className="text-sm text-gray-500">Ei yhdistetty</p>
          </div>
        </div>

        <GarminConnectForm
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['wearable-connection'] });
          }}
        />
      </div>
    );
  }

  const statusColors = {
    pending: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30',
    syncing: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
    success: 'text-green-600 bg-green-100 dark:bg-green-900/30',
    error: 'text-red-600 bg-red-100 dark:bg-red-900/30',
  };

  const statusLabels = {
    pending: 'Odottaa',
    syncing: 'Synkronoidaan',
    success: 'Synkronoitu',
    error: 'Virhe',
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-full">
            <Watch className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">
              {connection.provider.displayName}
            </h3>
            <p className="text-sm text-gray-500">{connection.username}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <span className="text-sm text-green-600">Yhdistetty</span>
        </div>
      </div>

      {/* Status */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Synkronoinnin tila</span>
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              statusColors[connection.syncStatus]
            }`}
          >
            {statusLabels[connection.syncStatus]}
          </span>
        </div>

        {connection.lastSyncAt && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Viimeksi synkronoitu</span>
            <span className="text-gray-700 dark:text-gray-300">
              {format(new Date(connection.lastSyncAt), 'd.M.yyyy HH:mm', {
                locale: fi,
              })}
            </span>
          </div>
        )}

        {connection.errorMessage && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {connection.errorMessage}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending || connection.syncStatus === 'syncing'}
          className="btn btn-primary flex-1"
        >
          {syncMutation.isPending || connection.syncStatus === 'syncing' ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Synkronoidaan...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Synkronoi nyt
            </>
          )}
        </button>

        <button
          onClick={() => {
            if (confirm('Haluatko varmasti poistaa Garmin-yhteyden?')) {
              disconnectMutation.mutate();
            }
          }}
          disabled={disconnectMutation.isPending}
          className="btn btn-danger"
        >
          {disconnectMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

export function WearableSettingsPage() {
  return (
    <div className="space-y-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/health"
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Wearable-asetukset
        </h1>
      </div>

      {/* Description */}
      <p className="text-gray-500 dark:text-gray-400">
        Yhdistä älylaite tai urheilusovellus synkronoidaksesi terveystietosi
        automaattisesti.
      </p>

      {/* Connection status */}
      <ConnectionStatus />

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-300 mb-2">
          Automaattinen synkronointi
        </h4>
        <p className="text-sm text-blue-700 dark:text-blue-400">
          Tietosi synkronoidaan automaattisesti joka päivä klo 21:00.
          Voit myös synkronoida manuaalisesti milloin tahansa.
        </p>
      </div>

      {/* Future providers */}
      <div className="card p-4 opacity-50">
        <h3 className="font-medium text-gray-900 dark:text-white mb-3">
          Tulossa pian
        </h3>
        <div className="space-y-2 text-sm text-gray-500">
          <p>- Polar Flow</p>
          <p>- Apple Health</p>
          <p>- Google Fit</p>
        </div>
      </div>
    </div>
  );
}
